import React from "react";
import { getTierConfig } from "shared/config/adaptiveLearningConfig";

/**
 * ProgressBar Component
 * Displays a progress bar with percentage and tier colors
 */
const ProgressBar = ({
  percentage,
  tier,
  showPercentage = true,
  height = "h-4",
  className = "",
}) => {
  const config = getTierConfig(tier);
  const safePercentage = Math.min(100, Math.max(0, percentage || 0));

  return (
    <div className={`w-full ${className}`}>
      <div
        className={`relative w-full ${height} bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden`}
      >
        {/* Progress fill */}
        <div
          className={`${height} rounded-full transition-all duration-500 ease-out`}
          style={{
            width: `${safePercentage}%`,
            background: `linear-gradient(90deg, ${config.gradientFrom} 0%, ${config.gradientTo} 100%)`,
            boxShadow: `0 0 10px ${config.color}40`,
          }}
        />

        {/* Percentage text overlay */}
        {showPercentage && (
          <div className="absolute inset-0 flex items-center justify-center">
            <span
              className="text-xs font-bold"
              style={{
                color: safePercentage > 50 ? "#ffffff" : config.color,
                textShadow:
                  safePercentage > 50
                    ? "0 1px 2px rgba(0,0,0,0.3)"
                    : "0 1px 2px rgba(255,255,255,0.8)",
              }}
            >
              {safePercentage.toFixed(1)}%
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * ProgressRing Component
 * Circular progress indicator for cards
 */
export const ProgressRing = ({ percentage, tier, size = 120, strokeWidth = 8 }) => {
  const config = getTierConfig(tier);
  const safePercentage = Math.min(100, Math.max(0, percentage || 0));
  
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (safePercentage / 100) * circumference;

  return (
    <div className="relative inline-flex items-center justify-center">
      <svg width={size} height={size} className="transform -rotate-90">
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="currentColor"
          strokeWidth={strokeWidth}
          fill="none"
          className="text-gray-200 dark:text-gray-700"
        />
        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={config.color}
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          className="transition-all duration-500 ease-out"
          style={{
            filter: `drop-shadow(0 0 6px ${config.color}80)`,
          }}
        />
      </svg>
      {/* Center text */}
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-2xl font-extrabold" style={{ color: config.color }}>
          {safePercentage.toFixed(0)}%
        </span>
        <span className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          Mastery
        </span>
      </div>
    </div>
  );
};

export default ProgressBar;


